import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Plus, Receipt } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Can } from '@/components/Can';
import { FilterBar } from '@/components/FilterBar';
import { ImportExportMenu } from '@/components/ImportExportMenu';
import { RefreshButton } from '@/components/RefreshButton';
import { Badge, Button, DataTable, EmptyState, PageHeader, StateBadge, } from '@/components/ui';
import { api } from '@/lib/api';
const STATES = [
    'draft', 'submitted', 'approved', 'posted',
    'partially_paid', 'paid', 'overdue', 'voided',
];
export function BillListPage() {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ state: '', from: '', to: '', q: '' });
    const state = filters.state ?? '';
    const from = filters.from ?? '';
    const to = filters.to ?? '';
    const search = (filters.q ?? '').toLowerCase().trim();
    const bills = useQuery({
        queryKey: ['bills', state, from, to],
        queryFn: async () => {
            const p = new URLSearchParams();
            if (state)
                p.set('state', state);
            if (from)
                p.set('from', from);
            if (to)
                p.set('to', to);
            return (await api.get(`/purchases/bills/?${p.toString()}`)).data;
        },
    });
    const filtered = useMemo(() => {
        if (!search)
            return bills.data ?? [];
        return (bills.data ?? []).filter((b) => (b.number ?? '').toLowerCase().includes(search)
            || (b.vendor_invoice_number ?? '').toLowerCase().includes(search)
            || (b.vendor_name ?? '').toLowerCase().includes(search));
    }, [bills.data, search]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(PageHeader, { title: "Bills", description: "Vendor invoices, approvals, and payments.", actions: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RefreshButton, { queryKeys: [['bills']], label: "Fetch" }), _jsx(ImportExportMenu, { resource: "/purchases/bills", canImport: false }), _jsx(Can, { code: "purchases.bill.create", children: _jsx(Button, { leftIcon: _jsx(Plus, { className: "h-4 w-4" }), onClick: () => navigate('/purchases/bills/new'), children: "New bill" }) })] }) }), _jsx(FilterBar, { filters: [
                    { key: 'q', kind: 'search', placeholder: 'Search number / vendor ref / vendor…' },
                    { key: 'state', kind: 'select', label: 'State',
                        options: STATES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') })) },
                    { key: 'from', kind: 'date', label: 'From' },
                    { key: 'to', kind: 'date', label: 'To' },
                ], value: filters, onChange: setFilters }), _jsx(DataTable, { rows: filtered, loading: bills.isLoading, rowKey: (r) => r.id, onRowClick: (b) => navigate(`/purchases/bills/${b.id}`), empty: _jsx(EmptyState, { icon: _jsx(Receipt, { className: "h-6 w-6" }), title: "No bills yet", description: "Record vendor invoices to track AP, WHT and payments." }), columns: [
                    { key: 'num', header: 'Number',
                        render: (b) => _jsx("code", { className: "font-mono text-xs", children: b.number || '—' }) },
                    { key: 'date', header: 'Date',
                        render: (b) => _jsx("span", { className: "text-muted-foreground", children: b.date }) },
                    { key: 'vref', header: 'Vendor ref',
                        render: (b) => _jsx("span", { className: "font-medium", children: b.vendor_invoice_number || '(no vendor ref)' }) },
                    { key: 'total', header: 'Total', align: 'right',
                        render: (b) => _jsxs("span", { className: "font-mono", children: [b.grand_total, " ", b.currency] }) },
                    { key: 'wht', header: 'WHT',
                        render: (b) => Number(b.wht_total) > 0
                            ? _jsxs(Badge, { tone: "info", children: ["WHT ", b.wht_total] })
                            : null },
                    { key: 'state', header: 'State', render: (b) => _jsx(StateBadge, { state: b.state }) },
                    { key: 'due', header: 'Due', render: (b) => b.due_date || _jsx("span", { className: "text-muted-foreground", children: "\u2014" }) },
                    { key: 'bal', header: 'Balance', align: 'right',
                        render: (b) => _jsx("span", { className: "font-mono font-semibold", children: b.balance_due }) },
                ] })] }));
}
